import React from 'react';
import { cn } from '../../utils/cn';
import { useLivePreviewStore } from '../../store/livePreviewStore';

export const DebugOverlay: React.FC = () => {
  const { debugOverlay, stats, playState } = useLivePreviewStore();

  if (!debugOverlay.enabled || playState === 'stopped') return null;

  const positionClasses = {
    'top-left': 'top-2 left-2',
    'top-right': 'top-2 right-2',
    'bottom-left': 'bottom-2 left-2',
    'bottom-right': 'bottom-2 right-2',
  };

  const formatMemory = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div
      className={cn(
        'absolute z-10 font-mono text-xs',
        'bg-black/70 backdrop-blur-sm',
        'rounded border border-zinc-700/50',
        'p-2 min-w-[140px]',
        positionClasses[debugOverlay.position]
      )}
      style={{ opacity: debugOverlay.opacity }}
    >
      <div className="space-y-1">
        {/* FPS */}
        {debugOverlay.showFps && (
          <div className="flex justify-between gap-4">
            <span className="text-zinc-400">FPS:</span>
            <span
              className={cn(
                'font-bold',
                stats.fps < 30 && 'text-red-400',
                stats.fps >= 30 && stats.fps < 55 && 'text-yellow-400',
                stats.fps >= 55 && 'text-green-400'
              )}
            >
              {stats.fps}
            </span>
          </div>
        )}

        {/* Frame Time */}
        {debugOverlay.showFrameTime && (
          <div className="flex justify-between gap-4">
            <span className="text-zinc-400">Frame:</span>
            <span className="text-white">
              {stats.frameTime.toFixed(2)} ms
            </span>
          </div>
        )}

        {/* Draw Calls */}
        {debugOverlay.showDrawCalls && (
          <div className="flex justify-between gap-4">
            <span className="text-zinc-400">Draw Calls:</span>
            <span className="text-white">{stats.drawCalls}</span>
          </div>
        )}

        {/* Object Count */}
        {debugOverlay.showObjectCount && (
          <div className="flex justify-between gap-4">
            <span className="text-zinc-400">Objects:</span>
            <span className="text-white">
              {stats.visibleObjects}/{stats.activeObjects}
            </span>
          </div>
        )}

        {/* Memory */}
        {debugOverlay.showMemory && (
          <div className="flex justify-between gap-4">
            <span className="text-zinc-400">Memory:</span>
            <span className="text-white">
              {formatMemory(stats.memoryUsed)}
            </span>
          </div>
        )}

        {/* Textures */}
        {debugOverlay.showTextures && (
          <div className="flex justify-between gap-4">
            <span className="text-zinc-400">Textures:</span>
            <span className="text-white">
              {stats.texturesLoaded} ({formatMemory(stats.textureMemory)})
            </span>
          </div>
        )}

        {/* Audio */}
        {debugOverlay.showAudio && (
          <div className="flex justify-between gap-4">
            <span className="text-zinc-400">Audio:</span>
            <span className="text-white">
              {stats.audioPlaying}/{stats.audioLoaded}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};
